import type { SponsoredDisplayCapabilities } from "@adpilot/shared";
import { useQuery } from "@tanstack/react-query";
import { type Dispatch, type SetStateAction, useEffect, useMemo, useRef } from "react";
import {
  fetchSponsoredDisplayAssets,
  fetchSponsoredDisplayAudienceBidRecommendations,
  fetchSponsoredDisplayAudienceRecommendations,
  fetchSponsoredDisplayBidRecommendations,
  fetchSponsoredDisplayContextualRecommendations,
  fetchSponsoredDisplayProductEligibility,
} from "@/lib/campaign-creation-api";
import { fetchPortfolios } from "@/lib/portfolio-api";
import { selectCreativeAssets } from "./creative-assets";
import type { SponsoredDisplayDraft } from "./sponsored-display-draft";

/**
 * Server data the SD form derives from its draft: portfolios, product eligibility,
 * targeting recommendations, bid suggestions for the active targets, and the
 * creative asset library filtered to what each creative slot accepts.
 */
export function useSponsoredDisplayQueries({
  shopId,
  initialPortfolioId,
  draft,
  setDraft,
  sponsoredDisplayCapabilities,
}: {
  shopId: string;
  initialPortfolioId?: string | null;
  draft: SponsoredDisplayDraft;
  setDraft: Dispatch<SetStateAction<SponsoredDisplayDraft>>;
  sponsoredDisplayCapabilities: SponsoredDisplayCapabilities;
}) {
  const asins = useMemo(
    () => [...new Set(draft.products.map((product) => product.asin))],
    [draft.products],
  );

  const portfolios = useQuery({
    queryKey: ["portfolios", "campaign-creation", shopId],
    queryFn: () => fetchPortfolios(),
  });
  // ADP-137: entry from a portfolio detail preselects that portfolio. Applied
  // once, only while the draft has no portfolio (a restored draft's explicit
  // choice wins); list membership doubles as the same-shop check.
  const portfolioPrefillApplied = useRef(false);
  useEffect(() => {
    if (portfolioPrefillApplied.current || !initialPortfolioId) return;
    const available = portfolios.data?.some(
      (portfolio) => portfolio.id === initialPortfolioId && portfolio.state !== "archived",
    );
    if (!available) return;
    portfolioPrefillApplied.current = true;
    setDraft((current) =>
      current.portfolioId == null ? { ...current, portfolioId: initialPortfolioId } : current,
    );
  }, [initialPortfolioId, portfolios.data, setDraft]);
  const eligibility = useQuery({
    queryKey: ["campaign-creation", "sd", "eligibility", shopId, draft.products],
    queryFn: () =>
      fetchSponsoredDisplayProductEligibility(
        shopId,
        draft.products.map((product) => ({ asin: product.asin, sku: product.sku })),
      ),
    enabled: draft.products.length > 0,
    retry: false,
  });
  const contextual = useQuery({
    queryKey: ["campaign-creation", "sd", "contextual", shopId, asins],
    queryFn: () => fetchSponsoredDisplayContextualRecommendations(shopId, asins),
    enabled: asins.length > 0,
    retry: false,
  });
  const audienceRecommendationsEnabled =
    sponsoredDisplayCapabilities.targeting.amazonAudience.state === "supported";
  const audience = useQuery({
    queryKey: ["campaign-creation", "sd", "audience", shopId],
    queryFn: () => fetchSponsoredDisplayAudienceRecommendations(shopId),
    enabled: audienceRecommendationsEnabled,
    retry: false,
  });
  const assets = useQuery({
    queryKey: ["campaign-creation", "sd", "assets", shopId],
    queryFn: () => fetchSponsoredDisplayAssets(shopId),
    enabled: draft.creative.logoEnabled || draft.creative.imageEnabled,
    retry: false,
  });

  const contextualRecommendationTargets = useMemo(
    () => draft.contextualTargets.slice(0, 100).map((row) => ({ ...row.target, bid: null })),
    [draft.contextualTargets],
  );
  const audienceRecommendationTargets = useMemo(
    () => draft.audienceTargets.slice(0, 100).map((row) => ({ ...row.target, bid: null })),
    [draft.audienceTargets],
  );
  const contextualBids = useQuery({
    queryKey: [
      "campaign-creation",
      "sd",
      "contextual-bids",
      shopId,
      draft.optimizationGoal,
      asins,
      contextualRecommendationTargets,
    ],
    queryFn: () =>
      fetchSponsoredDisplayBidRecommendations(shopId, {
        advertisedAsins: asins,
        optimizationGoal: draft.optimizationGoal,
        creativeType: null,
        targets: contextualRecommendationTargets,
      }),
    enabled: asins.length > 0 && contextualRecommendationTargets.length > 0,
    retry: false,
  });
  const audienceBids = useQuery({
    queryKey: [
      "campaign-creation",
      "sd",
      "audience-bids",
      shopId,
      draft.optimizationGoal,
      asins,
      audienceRecommendationTargets,
    ],
    queryFn: () =>
      fetchSponsoredDisplayAudienceBidRecommendations(shopId, {
        advertisedAsins: asins,
        optimizationGoal: draft.optimizationGoal,
        creativeType: null,
        targets: audienceRecommendationTargets,
      }),
    enabled: asins.length > 0 && audienceRecommendationTargets.length > 0,
    retry: false,
  });

  const activeBids = draft.targetingMode === "audience" ? audienceBids.data : contextualBids.data;
  const ineligible =
    eligibility.data?.products.filter((product) => product.status === "INELIGIBLE") ?? [];
  const eligibilityWarnings =
    eligibility.data?.products.flatMap((product) =>
      product.status === "ELIGIBLE_WITH_WARNING"
        ? product.messages.map((message) => `${product.asin}: ${message}`)
        : [],
    ) ?? [];
  const { logoAssets, imageAssets } = selectCreativeAssets(
    assets.data,
    sponsoredDisplayCapabilities.limits,
  );

  return {
    asins,
    portfolios,
    eligibility,
    contextual,
    audience,
    audienceRecommendationsEnabled,
    assets,
    activeBids,
    ineligible,
    eligibilityWarnings,
    logoAssets,
    imageAssets,
  };
}
